import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Search } from 'lucide-react';
import BotAccessDiagramView from './BotAccessDiagramView';
import type { BotAccessDatabase, BotAccessGitHubRepo, BotAccessUser } from './botAccessTypes';
import {
  governanceFallbackUsers,
  mergeAccessUsers,
  normalizePersonName,
  sortAccessUsers,
  usersFromTelegramEntries,
} from './botAccessUsers';

export type { BotAccessUser } from './botAccessTypes';

export type BotAccessDiagramModalProps = {
  isOpen: boolean;
  onClose: () => void;
  botName: string;
  botIdentifier?: string;
  botAvatar?: string;
  githubSummary?: string;
  databaseSummary?: string;
  matchedRole?: string | null;
  repos: BotAccessGitHubRepo[];
  databases: BotAccessDatabase[];
  accessUsers?: BotAccessUser[];
  owner?: string;
  pilots?: string;
};

export default function BotAccessDiagramModal({
  isOpen,
  onClose,
  botName,
  botIdentifier,
  botAvatar,
  githubSummary,
  databaseSummary,
  matchedRole,
  repos,
  databases,
  accessUsers = [],
  owner,
  pilots,
}: BotAccessDiagramModalProps) {
  const [query, setQuery] = useState('');
  const [resolvedUsers, setResolvedUsers] = useState<BotAccessUser[]>(() =>
    mergeAccessUsers(accessUsers, governanceFallbackUsers(owner, pilots))
  );
  const [usersLoading, setUsersLoading] = useState(false);

  const sortedUsers = useMemo(() => sortAccessUsers(resolvedUsers), [resolvedUsers]);

  useEffect(() => {
    if (!isOpen) return;
    const identifier = botIdentifier || botName;
    if (!identifier) return;

    let cancelled = false;
    const controller = new AbortController();
    const fallback = governanceFallbackUsers(owner, pilots);
    setUsersLoading(true);

    const loadUsers = async () => {
      const base = `/api/prisme/openclaw-agents/${encodeURIComponent(identifier)}`;
      let fromUsersApi: BotAccessUser[] = [];
      let fromSnapshot: BotAccessUser[] = [];
      let fromTelegram: BotAccessUser[] = [];

      try {
        const usersRes = await fetch(`${base}/access-users`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (usersRes.ok) {
          const payload = await usersRes.json();
          if (Array.isArray(payload?.users)) fromUsersApi = payload.users;
        }
      } catch {
        /* endpoint optionnel */
      }

      try {
        const snapRes = await fetch(`${base}/access-snapshot`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (snapRes.ok) {
          const payload = await snapRes.json();
          if (Array.isArray(payload?.access_users)) fromSnapshot = payload.access_users;
        }
      } catch {
        /* ignore */
      }

      try {
        const entriesRes = await fetch(`${base}/entries?tab=telegram`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (entriesRes.ok) {
          const entries = await entriesRes.json();
          fromTelegram = usersFromTelegramEntries(entries);
        }
      } catch {
        /* ignore */
      }

      if (!cancelled) {
        const fromDb = mergeAccessUsers(fromUsersApi, fromSnapshot, fromTelegram);
        const missingFallback = fallback.filter(
          (user) => !fromDb.some((existing) => normalizePersonName(existing.name) === normalizePersonName(user.name))
        );
        setResolvedUsers(mergeAccessUsers(fromDb, missingFallback));
      }
    };

    void loadUsers().finally(() => {
      if (!cancelled) setUsersLoading(false);
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isOpen, botIdentifier, botName, owner, pilots]);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const verifiedCount = repos.filter((r) => r.verified).length;
  const privateCount = repos.filter((r) => r.private).length;
  const ownerCount = databases.filter((d) => d.is_owner).length;
  const connectCount = databases.filter((d) => d.can_connect).length;

  const q = query.trim().toLowerCase();
  const filteredRepos = useMemo(
    () =>
      !q
        ? repos
        : repos.filter((r) =>
            [r.repo, r.url, r.repo_path, r.remote_name, r.access_level].some((v) =>
              String(v || '').toLowerCase().includes(q)
            )
          ),
    [q, repos]
  );
  const filteredDatabases = useMemo(
    () =>
      !q
        ? databases
        : databases.filter((d) => [d.database, d.owner].some((v) => String(v || '').toLowerCase().includes(q))),
    [q, databases]
  );

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key="access-diagram"
          className="fixed inset-0 z-[120] flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-[#05070f]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute left-1/2 top-[-10%] h-[420px] w-[680px] -translate-x-1/2 rounded-full bg-indigo-600/20 blur-[140px]" />
            <div className="absolute -left-40 bottom-0 h-80 w-80 rounded-full bg-violet-600/15 blur-[120px]" />
            <div className="absolute -right-40 bottom-10 h-80 w-80 rounded-full bg-emerald-600/15 blur-[120px]" />
            <div
              className="absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(148,163,184,.18) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,.18) 1px, transparent 1px)',
                backgroundSize: '46px 46px',
                maskImage: 'radial-gradient(circle at 50% 30%, black, transparent 78%)',
              }}
            />
          </div>

          <motion.div
            className="relative z-10 flex min-h-0 flex-1 flex-col"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
          >
            <header className="shrink-0 space-y-4 px-6 py-5 md:px-10">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-black tracking-tight text-white md:text-4xl">Data Center</h1>
                  <p className="mt-1 text-sm text-slate-400">
                    Diagramme visuel des accès — bot {botName}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-white/10 bg-white/5 p-2.5 text-slate-300 transition hover:bg-white/15 hover:text-white"
                  aria-label="Fermer"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </header>

            <div className="shrink-0 px-6 md:px-10">
              <div className="relative mx-auto max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filtrer un dépôt ou une base…"
                  className="w-full rounded-xl border border-white/10 bg-slate-900/70 py-2 pl-10 pr-4 text-sm text-white outline-none placeholder:text-slate-500 focus:border-indigo-400/50"
                />
              </div>
            </div>

            <main className="min-h-0 flex-1 overflow-y-auto px-4 pb-10 pt-6 md:px-10">
              <div className="mx-auto max-w-6xl">
                <BotAccessDiagramView
                  botName={botName}
                  botAvatar={botAvatar}
                  users={sortedUsers}
                  usersLoading={usersLoading}
                  repos={filteredRepos}
                  reposTotal={repos.length}
                  databases={filteredDatabases}
                  databasesTotal={databases.length}
                  githubSummary={githubSummary}
                  databaseSummary={databaseSummary}
                  matchedRole={matchedRole}
                  verifiedCount={verifiedCount}
                  privateCount={privateCount}
                  ownerCount={ownerCount}
                  connectCount={connectCount}
                />
              </div>
            </main>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
